import { useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Loader2 } from "lucide-react";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Worker source: use unpkg CDN matched to the bundled pdfjs version. Keeping
// the worker on a CDN sidesteps a production bug where our static file server
// returns ``application/octet-stream`` for ``.mjs`` assets, which browsers
// refuse to execute as ES modules ("Failed to fetch dynamically imported
// module"). This file is itself lazy-loaded by ColumnistArticlePage so the
// worker URL only resolves when we actually render a PDF article — keeping
// the ~250 KB pdfjs core out of the initial bundle for HTML-bodied articles
// (which is the majority).
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * ArticlePdfViewer — extracted from ColumnistArticlePage so the heavy
 * ``react-pdf`` chunk only loads when a PDF article is actually opened.
 * Articles with ``article.source !== "pdf"`` render plain HTML and never
 * pull this code path.
 */
export default function ArticlePdfViewer({ pdfUrl }) {
  const [numPages, setNumPages] = useState(null);
  const [loadError, setLoadError] = useState(null);

  return (
    <div
      className="rounded-xl border border-slate-800 bg-slate-100 overflow-hidden flex flex-col items-center"
      data-testid="columnist-pdf-viewer"
    >
      <Document
        file={pdfUrl}
        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
        onLoadError={(err) => setLoadError(err?.message || "Erro ao abrir PDF")}
        loading={
          <div className="py-16 text-slate-600 inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Carregando PDF…
          </div>
        }
        error={
          <div className="py-16 text-red-700 text-sm text-center px-6">
            Não foi possível abrir o PDF inline. {loadError}<br/>
            <a href={pdfUrl} className="text-sky-700 underline">Abrir em nova aba</a>
          </div>
        }
        className="w-full"
      >
        {numPages && Array.from({ length: numPages }, (_, i) => (
          <div key={`pg_${i + 1}`} className="border-b border-slate-300 last:border-b-0" data-testid={`columnist-page-${i + 1}`}>
            <Page
              pageNumber={i + 1}
              width={Math.min(typeof window !== "undefined" ? window.innerWidth - 80 : 760, 760)}
              renderAnnotationLayer={false}
              renderTextLayer={true}
            />
          </div>
        ))}
      </Document>
    </div>
  );
}
